import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Descriptions,
  Table,
  Spin,
  Space,
  Divider,
  Avatar,
  List,
  Button,
  Statistic,
  Alert,
  Tooltip,
  Empty,
} from 'antd';
import {
  BankOutlined,
  TeamOutlined,
  WarningOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  StarFilled,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';
import AIInsightsCard from '../components/AIInsightsCard';
import CompanyNewsCard from '../components/CompanyNewsCard';
import BuyingCommitteeCard from '../components/BuyingCommitteeCard';
import EstabelecimentosCard from '../components/EstabelecimentosCard';

const { Title, Text, Paragraph } = Typography;

interface CNPJDetailPageProps {
  cnpj: string;
  onBack?: () => void;
}

interface Socio {
  nome_socio: string;
  qualificacao_socio: string;
  data_entrada_sociedade: string;
  faixa_etaria: string;
  cnpj_cpf_socio: string;
}

interface Divida {
  id: string;
  tipo_divida: string;
  valor_consolidado: number;
  situacao_inscricao: string;
  indicador_ajuizado: string;
  data_inscricao: string;
  receita_principal: string;
  tempo_inscricao_anos: number;
}

interface PGFNResumo {
  linha: string;
  score_risco: number;
  valor_total: number;
  total_dividas: number;
  qtd_ajuizadas: number;
  qtd_nao_ajuizadas: number;
  tempo_divida_mais_antiga_anos: number;
  tipos_divida: string[];
  alguma_ajuizada: boolean;
  nome_devedor?: string;
  uf?: string;
}

interface EmpresaData {
  cnpj: string;
  cnpj_basico: string;
  empresa?: any;
  socios: Socio[];
  pgfn: {
    resumo: PGFNResumo | null;
    dividas: Divida[];
  };
}

const CNPJDetailPage: React.FC<CNPJDetailPageProps> = ({ cnpj, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EmpresaData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    fetchData();
  }, [cnpj]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_SERVIDOR2.BASE}/api/v1/cnpj/${cnpj}`);
      setData(response.data);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados do CNPJ');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '';
    const clean = cnpj.replace(/\D/g, '');
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const getLinhaColor = (linha: string) => {
    switch (linha) {
      case 'A': return '#52c41a';
      case 'B': return '#faad14';
      case 'C': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const columnsDividas = [
    {
      title: 'Tipo',
      dataIndex: 'tipo_divida',
      key: 'tipo_divida',
      width: 80,
      render: (tipo: string) => (
        <Tag color={tipo === 'FGTS' ? 'blue' : tipo === 'PREV' ? 'purple' : 'cyan'}>
          {tipo}
        </Tag>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'valor_consolidado',
      key: 'valor_consolidado',
      width: 130,
      render: (value: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: 'Situação',
      dataIndex: 'situacao_inscricao',
      key: 'situacao_inscricao',
      width: 120,
      render: (text: string) => (
        <Tag color={text === 'INSCRITA' ? 'blue' : text?.includes('PARCEL') ? 'green' : 'default'}>
          {text?.substring(0, 15)}
        </Tag>
      ),
    },
    {
      title: 'Ajuizado',
      dataIndex: 'indicador_ajuizado',
      key: 'indicador_ajuizado',
      width: 80,
      render: (text: string) => (
        <Tag color={text === 'SIM' ? 'red' : 'green'} icon={text === 'SIM' ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Tempo',
      dataIndex: 'tempo_inscricao_anos',
      key: 'tempo_inscricao_anos',
      width: 70,
      render: (tempo: number) => (
        <Text type={tempo > 10 ? 'danger' : tempo > 5 ? 'warning' : 'secondary'}>
          {tempo?.toFixed(1)}a
        </Text>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Carregando dados do CNPJ...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert
        message="Erro"
        description={error || 'Não foi possível carregar os dados'}
        type="error"
        showIcon
        action={
          <Button onClick={onBack} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>
        }
      />
    );
  }

  const { pgfn, socios, empresa } = data;
  const resumo = pgfn?.resumo;
  const dividas = pgfn?.dividas || [];
  const nomeDevedor = resumo?.nome_devedor || empresa?.razao_social || 'Empresa';
  const ufEmpresa = resumo?.uf || empresa?.uf || '';

  return (
    <div style={{ padding: '16px 24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {onBack && (
              <Button icon={<ArrowLeftOutlined />} onClick={onBack} type="text">
                Voltar
              </Button>
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {nomeDevedor}
                </Title>
                {empresa?.situacao_cadastral === 'ATIVA' || !empresa?.situacao_cadastral ? (
                  <Tag color="green">ATIVA</Tag>
                ) : (
                  <Tag color="red">{empresa.situacao_cadastral}</Tag>
                )}
                {ufEmpresa && <Tag>{ufEmpresa}</Tag>}
              </div>
              <Space style={{ marginTop: 4 }}>
                <Text copyable={{ text: cnpj }} style={{ fontFamily: 'monospace', color: '#666' }}>
                  {formatCNPJ(cnpj)}
                </Text>
                {empresa?.email && (
                  <Text type="secondary">
                    <MailOutlined /> {empresa.email}
                  </Text>
                )}
                {empresa?.telefone && (
                  <Text type="secondary">
                    <PhoneOutlined /> {empresa.telefone}
                  </Text>
                )}
              </Space>
            </div>
          </div>
          <Space>
            <Button 
              type={isFavorite ? 'primary' : 'default'}
              icon={isFavorite ? <StarFilled /> : <StarOutlined />}
              onClick={() => setIsFavorite(!isFavorite)}
            >
              Favoritar
            </Button>
            <Button>Buscar empresas</Button>
          </Space>
        </div>
      </Card>

      {/* Layout 3 Colunas estilo Cortex */}
      <Row gutter={16}>
        {/* Coluna Esquerda - Resumo + Atividade */}
        <Col xs={24} lg={8}>
          {/* Resumo da Empresa (IA) */}
          <AIInsightsCard 
            cnpj={cnpj} 
            tipo="resumo" 
            titulo="Resumo da Empresa"
          />

          {/* Dados da Empresa */}
          <Card 
            title="Dados da Empresa" 
            size="small" 
            style={{ marginBottom: 16 }}
          >
            {empresa?.capital_social && empresa.capital_social > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Text strong><DollarOutlined /> Capital Social: </Text>
                <Text style={{ fontSize: 15, color: '#1890ff' }}>
                  {formatCurrency(empresa.capital_social)}
                </Text>
              </div>
            )}
            {empresa?.porte_empresa && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Porte: </Text>
                <Tag color="blue">{empresa.porte_empresa}</Tag>
              </div>
            )}
            {empresa?.natureza_juridica && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Natureza: </Text>
                <Text type="secondary">{empresa.natureza_juridica}</Text>
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <Text strong>CNAE: </Text>
              <Text>{empresa?.cnae_fiscal_principal || 'N/A'}</Text>
            </div>
            {empresa?.cnae_descricao && (
              <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}>
                {empresa.cnae_descricao}
              </Paragraph>
            )}
            {empresa?.data_inicio_atividade && (
              <div style={{ marginTop: 8 }}>
                <Text strong>Início: </Text>
                <Text type="secondary">
                  {empresa.data_inicio_atividade.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}
                </Text>
              </div>
            )}
            {(empresa?.logradouro || empresa?.municipio) && (
              <div style={{ marginTop: 8 }}>
                <Text strong><EnvironmentOutlined /> Endereço: </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {[empresa?.logradouro, empresa?.numero, empresa?.bairro, empresa?.municipio, empresa?.uf].filter(Boolean).join(', ')}
                </Text>
              </div>
            )}
          </Card>

          {/* Estabelecimentos (Matriz + Filiais) */}
          <EstabelecimentosCard cnpj={cnpj} />

          {/* Quadro Societário Simples */}
          <Card 
            title={<><TeamOutlined /> Quadro Societário</>}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {socios.length > 0 ? (
              <List
                size="small"
                dataSource={socios.slice(0, 6)}
                renderItem={(socio) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          size="small"
                          style={{ 
                            backgroundColor: socio.qualificacao_socio?.toLowerCase().includes('administrador') 
                              ? '#1890ff' : '#87d068' 
                          }}
                        >
                          {socio.nome_socio?.charAt(0)}
                        </Avatar>
                      }
                      title={<Text style={{ fontSize: 13 }}>{socio.nome_socio}</Text>}
                      description={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {socio.qualificacao_socio}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Sem sócios" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>

        {/* Coluna Central - Inteligência Comercial + Notícias */}
        <Col xs={24} lg={8}>
          {/* Inteligência Comercial (IA) */}
          <AIInsightsCard 
            cnpj={cnpj} 
            tipo="comercial" 
            titulo="Inteligência Comercial (Beta)"
          />

          {/* Notícias da Empresa */}
          <CompanyNewsCard cnpj={cnpj} />

          {/* Resumo PGFN */}
          {resumo && (
            <Card 
              title={<><WarningOutlined style={{ color: '#faad14' }} /> Situação PGFN</>}
              size="small"
              extra={
                <Tag color={getLinhaColor(resumo.linha)} style={{ fontWeight: 'bold' }}>
                  Linha {resumo.linha} | Score {resumo.score_risco}
                </Tag>
              }
            >
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Statistic
                    title="Valor Total"
                    value={resumo.valor_total}
                    precision={0}
                    prefix="R$"
                    valueStyle={{ fontSize: 18, color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Dívidas"
                    value={resumo.total_dividas}
                    valueStyle={{ fontSize: 18 }}
                    suffix={<Text type="secondary" style={{ fontSize: 12 }}>({resumo.qtd_ajuizadas} ajuiz.)</Text>}
                  />
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <Space wrap size={4}>
                {resumo.tipos_divida?.map(tipo => (
                  <Tag key={tipo} color={tipo === 'FGTS' ? 'blue' : tipo === 'PREV' ? 'purple' : 'cyan'}>
                    {tipo}
                  </Tag>
                ))}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <ClockCircleOutlined /> {resumo.tempo_divida_mais_antiga_anos?.toFixed(1)} anos
                </Text>
              </Space>
            </Card>
          )}
        </Col>

        {/* Coluna Direita - Buying Committee + Contatos */}
        <Col xs={24} lg={8}>
          {/* Buying Committee */}
          <BuyingCommitteeCard cnpj={cnpj} />

          {/* Dívidas Detalhadas */}
          <Card 
            title={<><DollarOutlined /> Dívidas Individuais ({dividas.length})</>}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {dividas.length > 0 ? (
              <Table
                columns={columnsDividas}
                dataSource={dividas.slice(0, 5)}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 500 }}
              />
            ) : (
              <Empty description="Nenhuma dívida" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
            {dividas.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Button type="link" size="small">
                  Ver todas ({dividas.length})
                </Button>
              </div>
            )}
          </Card>

          {/* Exportar Relatório */}
          <Card size="small">
            <Button 
              type="primary" 
              block 
              icon={<DownloadOutlined />}
              style={{ background: '#1890ff' }}
            >
              Exportar Relatório
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CNPJDetailPage;
